import React from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { renderMarkdown } from "@impero-com/ui";
import { Typography, Input } from "antd";

import { MultiInputHelper } from "utils/multiInputHelper";

import { BaseFormDisplay } from "./formDisplay";

import { BaseFormField } from ".";

const helper: MultiInputHelper<string, React.ReactNode> = new MultiInputHelper({
    isEqual: (a: string, b: string) => a === b,
    isBlank: (val: string) => /^\s*$/.test(val),
    display: (value: string) => renderMarkdown(value),
});

type MultiStringInputProps =
    | ({ type: "CREATE" } & WrappedWriteProps)
    | ({ type: "EDIT" } & WrappedWriteProps)
    | ({ type: "READ_ONLY" } & WrappedReadProps);

type Wrapped<T> = T & { label: string };

interface ReadProps {
    name: string;
    values?: string[];
}

type WrappedReadProps = Wrapped<ReadProps>;

interface WriteProps extends ReadProps {
    onChange: (v: string) => void;
}

type WrappedWriteProps = Wrapped<WriteProps>;

class MultiStringInput extends React.PureComponent<MultiStringInputProps> {
    render() {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const { label, name, values } = props;
                return (
                    <WrappedReadOnlyStringInput
                        label={label}
                        name={name}
                        values={values}
                    />
                );
            }
            case "EDIT": {
                const { label, name, values, onChange } = props;
                return (
                    <WrappedEditStringInput
                        label={label}
                        name={name}
                        values={values}
                        onChange={onChange}
                    />
                );
            }
            case "CREATE": {
                const { label, name, values, onChange } = props;
                return (
                    <WrappedCreateStringInput
                        label={label}
                        name={name}
                        values={values}
                        onChange={onChange}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    }
}

type RawReadOnlyStringInputProps = ReadProps;

class RawReadOnlyStringInput extends React.PureComponent<RawReadOnlyStringInputProps> {
    render() {
        const { value, type } = helper.deriveReadOnlyValueAndType(
            this.props.values
        );
        return <Typography.Text type={type}>{value}</Typography.Text>;
    }
}

interface RawEditableStringInputProps extends WriteProps {
    autoFocus?: boolean;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

interface RawEditableStringInputState {
    derivedValue: string;
    trackedValue: string;
    placeholder: string;
}

class RawEditableStringInput extends React.Component<
    RawEditableStringInputProps,
    RawEditableStringInputState
> {
    state: RawEditableStringInputState = {
        derivedValue: "",
        trackedValue: "",
        placeholder: "",
    };

    static getDerivedStateFromProps(
        props: RawEditableStringInputProps,
        prevState: RawEditableStringInputState
    ) {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            props.values
        );
        if (value !== prevState.derivedValue) {
            return {
                derivedValue: value,
                trackedValue: value,
                placeholder,
            };
        }
        return null;
    }

    render() {
        const { props, state } = this;
        const { autoFocus, onFocus } = props;
        const { trackedValue, placeholder } = state;

        return (
            <Input
                placeholder={placeholder}
                autoFocus={autoFocus}
                value={trackedValue}
                onChange={this.onChange}
                onFocus={onFocus}
                onBlur={this.onBlur}
            />
        );
    }

    onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            trackedValue: event.target.value,
        });
    };

    onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        this.props.onBlur?.(event);
        if (this.didChange()) {
            this.props.onChange?.(this.state.trackedValue);
        }
    };

    private didChange() {
        return this.state.derivedValue !== this.state.trackedValue;
    }
}

class WrappedReadOnlyStringInput extends React.PureComponent<WrappedReadProps> {
    render() {
        const { label, name, values } = this.props;
        return (
            <BaseFormDisplay label={label}>
                <RawReadOnlyStringInput name={name} values={values} />
            </BaseFormDisplay>
        );
    }
}

interface WrappedEditStringInputState {
    isEditing: boolean;
}

class WrappedEditStringInput extends React.Component<
    WrappedWriteProps,
    WrappedEditStringInputState
> {
    state = {
        isEditing: false,
    };

    render() {
        const { label, name, values, onChange } = this.props;
        const { isEditing } = this.state;

        return (
            <BaseFormField
                getComponent={() => (
                    <RawEditableStringInput
                        name={name}
                        autoFocus={true}
                        values={values}
                        onChange={onChange}
                        onBlur={this.onBlur}
                    />
                )}
                getReadonlyComponent={() => (
                    <RawReadOnlyStringInput name={name} values={values} />
                )}
                onClick={this.onClick}
                hasFeedback={false}
                label={label}
                readonly={!isEditing}
            />
        );
    }

    onBlur = (_: React.FocusEvent<HTMLInputElement>) => {
        this.setState({
            isEditing: false,
        });
    };

    onClick = () => {
        this.setState({
            isEditing: true,
        });
    };
}

class WrappedCreateStringInput extends React.PureComponent<WrappedWriteProps> {
    render() {
        const { label, name, values, onChange } = this.props;
        return (
            <BaseFormField
                getComponent={() => (
                    <RawEditableStringInput
                        name={name}
                        values={values}
                        onChange={onChange}
                    />
                )}
                hasFeedback={false}
                label={label}
            />
        );
    }
}

export { MultiStringInput, type MultiStringInputProps };
